import { useEffect, useState } from "react";

interface InputProps {
    label: string;
    error?: string;
    type?: string;
    name: string;
    required?: boolean;
    placeholder?: string;
    value?: string | number;
    onChange?: (value: number | undefined) => void;
    disabled?: boolean;
    allowNegative?: boolean;
    currency?: string;
}

export default function PriceInput({
                                       label,
                                       error,
                                       name,
                                       placeholder,
                                       required,
                                       value,
                                       onChange,
                                       disabled = false,
                                       currency = "USD",
                                       allowNegative = false,
                                       ...rest
                                   }: InputProps) {
    const [rawValue, setRawValue] = useState<string>(
        value !== undefined && value !== "" ? String(value) : ""
    );

    useEffect(() => {
        if (value === undefined || value === "") {
            if (rawValue !== "") setRawValue("");
            return;
        }
        const current = parseFloat(rawValue);
        // Don't clobber user's in-progress typing (e.g. ".", "-") with the parent's coerced value
        if (isNaN(current) && rawValue !== "") return;
        // Don't push "0" back into a field the user just cleared
        if (rawValue === "" && Number(value) === 0) return;
        if (isNaN(current) || current !== Number(value)) {
            setRawValue(String(value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        const raw = e.target.value;
        setRawValue(raw);
        let normalized = raw;
        if (/^\.\d+/.test(normalized)) normalized = "0" + normalized;
        else if (/^-\.\d+/.test(normalized)) normalized = normalized.replace(/^-\./, "-0.");
        const num = parseFloat(normalized);
        if (onChange) {
            onChange(isNaN(num) ? undefined : num);
        }
    };

    const pattern = allowNegative
        ? "^-?(\\d+\\.?\\d{0,2}|\\.\\d{1,2})$"
        : "^(\\d+\\.?\\d{0,2}|\\.\\d{1,2})$";
    const validationTitle = allowNegative
        ? "Enter a price with up to 2 decimal places"
        : "Enter a non-negative price with up to 2 decimal places";

    return (
        <div>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                    type="text"
                    inputMode="decimal"
                    pattern={pattern}
                    title={validationTitle}
                    name={name}
                    id={name}
                    className="block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-red-500 focus:ring-red-500 sm:text-sm"
                    placeholder={placeholder || "0.00"}
                    value={rawValue}
                    onChange={handleChange}
                    aria-describedby="price-currency"
                    disabled={disabled}
                    {...rest}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <span className="text-gray-500 sm:text-sm" id="price-currency">
            {currency}
          </span>
                </div>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
    );
}
